import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
    const [info, setInfo] = useState(null);
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const attemptsRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            if (attemptsRef.current >= 8) {
                setStatus("failed");
                return;
            }
            attemptsRef.current += 1;
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setInfo(data);
                    setStatus("paid");
                    if (user) refresh();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                setTimeout(poll, 2000);
            }
        };
        if (sessionId) poll();
        else setStatus("failed");
        return () => {
            cancelled = true;
        };
    }, [sessionId, user, refresh]);

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-6 py-12" data-testid="checkout-success-page">
            <Card className="w-full max-w-lg border-border">
                <CardContent className="p-10 text-center">
                    {status === "polling" && (
                        <>
                            <Loader2 className="h-10 w-10 text-primary mx-auto animate-spin" />
                            <h2 className="font-display text-2xl text-primary mt-6">
                                Confirming your enrollment…
                            </h2>
                            <p className="text-muted-foreground mt-3 text-sm">
                                Hold tight while we verify your payment with Stripe.
                            </p>
                        </>
                    )}
                    {status === "paid" && (
                        <>
                            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
                            <h2 className="font-display text-3xl text-primary mt-6" data-testid="checkout-success-heading">
                                You're enrolled.
                            </h2>
                            <p className="text-foreground/75 mt-3">
                                Welcome to Cambridge. An onboarding email has been sent.
                            </p>
                            {info?.generated_password && (
                                <div className="mt-6 p-4 rounded-sm bg-secondary/50 border border-border text-left text-sm">
                                    <div className="font-semibold mb-1">Your account</div>
                                    <div>
                                        Email: <span className="font-mono">{info.user_email}</span>
                                    </div>
                                    <div>
                                        Temporary password:{" "}
                                        <span className="font-mono">{info.generated_password}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-2">
                                        (Email delivery is currently mocked for this demo.)
                                    </div>
                                </div>
                            )}
                            <div className="mt-8 flex justify-center gap-3">
                                <Button
                                    onClick={() => navigate(user ? "/dashboard" : "/login")}
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="go-to-dashboard-btn"
                                >
                                    {user ? "Go to dashboard" : "Log in"}
                                </Button>
                            </div>
                        </>
                    )}
                    {(status === "failed" || status === "expired") && (
                        <>
                            <XCircle className="h-10 w-10 text-destructive mx-auto" />
                            <h2 className="font-display text-2xl text-primary mt-6">
                                {status === "expired" ? "Session expired" : "Payment unconfirmed"}
                            </h2>
                            <p className="text-muted-foreground mt-3 text-sm">
                                Please try again or contact support.
                            </p>
                            <Button asChild className="mt-6" variant="outline">
                                <Link to="/">Back to catalog</Link>
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
